import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import EntityDocuments from "@/components/EntityDocuments";
import { Plane, Save, ArrowLeft, Plus, Trash2, Scale, Gauge, Ruler, FileText } from "lucide-react";

const AIRCRAFT_DOCS = [
  { key: "manuel_vol", label: "Manuel de vol" },
  { key: "fiche_pesee", label: "Fiche de pesée" },
  { key: "immatriculation", label: "Certificat d'immatriculation" },
  { key: "acoustique", label: "Certificat acoustique" },
  { key: "cdn", label: "CDN / CEN" },
  { key: "licence_station", label: "Licence de station d'aéronef" },
  { key: "assurance", label: "Assurance" },
];

const DEFAULT = {
  registration: "",
  model: "",
  tasCruise: 110,
  fuelBurn: 32,
  fuelCapacity: 110,
  emptyWeight: 620,
  emptyArm: 0.33,
  fuelArm: 0.9,
  mtow: 1000,
  takeoffDist: 400,
  landingDist: 350,
  stations: [
    { name: "Sièges avant", arm: 0.9 },
    { name: "Sièges arrière", arm: 1.9 },
    { name: "Bagages", arm: 2.5 },
  ],
  envelope: [
    { cg: 0.205, weight: 500 },
    { cg: 0.205, weight: 900 },
    { cg: 0.45, weight: 1000 },
    { cg: 0.53, weight: 1000 },
    { cg: 0.53, weight: 500 },
  ],
};

const Field = ({ label, unit, ...props }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider text-slate-400">
      {label} {unit && <span className="text-slate-600">({unit})</span>}
    </Label>
    <Input {...props} className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="glass rounded-2xl p-6">
    <h2 className="mb-4 flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-cyan-400">
      <Icon className="h-4 w-4" /> {title}
    </h2>
    {children}
  </div>
);

export default function AircraftForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/aircraft/${id}`).then((r) => setForm({ ...DEFAULT, ...r.data }));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => (e) => set(k, e.target.value === "" ? "" : Number(e.target.value));

  const setStation = (i, key, v) =>
    setForm((f) => {
      const stations = [...f.stations];
      stations[i] = { ...stations[i], [key]: key === "arm" ? Number(v) : v };
      return { ...f, stations };
    });
  const addStation = () => setForm((f) => ({ ...f, stations: [...f.stations, { name: "", arm: 0 }] }));
  const removeStation = (i) => setForm((f) => ({ ...f, stations: f.stations.filter((_, x) => x !== i) }));

  const setEnv = (i, key, v) =>
    setForm((f) => {
      const envelope = [...f.envelope];
      envelope[i] = { ...envelope[i], [key]: Number(v) };
      return { ...f, envelope };
    });
  const addEnv = () => setForm((f) => ({ ...f, envelope: [...f.envelope, { cg: 0, weight: 0 }] }));
  const removeEnv = (i) => setForm((f) => ({ ...f, envelope: f.envelope.filter((_, x) => x !== i) }));

  const save = async () => {
    if (!form.registration) return toast.error("Immatriculation requise");
    setSaving(true);
    try {
      if (id) {
        await api.put(`/aircraft/${id}`, form);
        toast.success("Avion enregistré");
        navigate("/aircraft");
      } else {
        const { data } = await api.post("/aircraft", form);
        toast.success("Avion créé — vous pouvez joindre les documents");
        navigate(`/aircraft/${data.id}`);
      }
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/aircraft")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <Button data-testid="save-aircraft-button" onClick={save} disabled={saving}
          className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          <Save className="mr-1.5 h-4 w-4" /> Enregistrer
        </Button>
      </div>

      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
        <Plane className="h-3.5 w-3.5" /> {id ? "Modifier l'avion" : "Nouvel avion"}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section icon={Plane} title="Identification">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Immatriculation" value={form.registration} onChange={(e) => set("registration", e.target.value.toUpperCase())} data-testid="aircraft-registration-input" placeholder="F-GXXX" />
            <Field label="Type / Modèle" value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="aircraft-model-input" placeholder="DR400-120" />
          </div>
        </Section>

        <Section icon={Gauge} title="Performances">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field label="TAS croisière" unit="kt" type="number" value={form.tasCruise} onChange={num("tasCruise")} data-testid="aircraft-tas-input" />
            <Field label="Conso" unit="L/h" type="number" value={form.fuelBurn} onChange={num("fuelBurn")} data-testid="aircraft-burn-input" />
            <Field label="Capacité carbu" unit="L" type="number" value={form.fuelCapacity} onChange={num("fuelCapacity")} data-testid="aircraft-fuelcap-input" />
            <Field label="Carbu non utilisable" unit="L" type="number" value={form.unusableFuelL ?? 0} onChange={num("unusableFuelL")} data-testid="aircraft-unusable-fuel-input" />
          </div>
        </Section>

        <Section icon={Scale} title="Masse & bras de levier">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Masse à vide" unit="kg" type="number" value={form.emptyWeight} onChange={num("emptyWeight")} data-testid="aircraft-empty-weight-input" />
            <Field label="Bras masse à vide" unit="m" type="number" step="0.001" value={form.emptyArm} onChange={num("emptyArm")} data-testid="aircraft-empty-arm-input" />
            <Field label="Bras carburant" unit="m" type="number" step="0.001" value={form.fuelArm} onChange={num("fuelArm")} />
            <Field label="MTOW" unit="kg" type="number" value={form.mtow} onChange={num("mtow")} data-testid="aircraft-mtow-input" />
          </div>
        </Section>

        <Section icon={Ruler} title="Distances de base">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Distance décollage" unit="m" type="number" value={form.takeoffDist} onChange={num("takeoffDist")} />
            <Field label="Distance atterrissage" unit="m" type="number" value={form.landingDist} onChange={num("landingDist")} />
          </div>
        </Section>
      </div>

      <Section icon={Scale} title="Stations de chargement (bras de levier)">
        <div className="space-y-3">
          {form.stations.map((s, i) => (
            <div key={i} className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Nom station</Label>
                <Input value={s.name} onChange={(e) => setStation(i, "name", e.target.value)}
                  className="mt-1 border-slate-700 bg-slate-900/60 text-slate-100" placeholder="Sièges avant" data-testid={`station-name-${i}`} />
              </div>
              <div className="w-40">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Bras (m)</Label>
                <Input type="number" step="0.001" value={s.arm} onChange={(e) => setStation(i, "arm", e.target.value)}
                  className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`station-arm-${i}`} />
              </div>
              <button onClick={() => removeStation(i)} className="mb-1 rounded-lg p-2.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button onClick={addStation} variant="outline" size="sm" data-testid="add-station-button"
          className="mt-4 border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
          <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter une station
        </Button>
      </Section>

      <Section icon={Scale} title="Enveloppe de centrage (points cg / masse)">
        <p className="mb-3 text-xs text-slate-500">Polygone de l'enveloppe certifiée : couples bras (m) / masse (kg), dans l'ordre du contour.</p>
        <div className="space-y-3">
          {form.envelope.map((p, i) => (
            <div key={i} className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">CG / Bras (m)</Label>
                <Input type="number" step="0.001" value={p.cg} onChange={(e) => setEnv(i, "cg", e.target.value)}
                  className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`env-cg-${i}`} />
              </div>
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Masse (kg)</Label>
                <Input type="number" value={p.weight} onChange={(e) => setEnv(i, "weight", e.target.value)}
                  className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`env-weight-${i}`} />
              </div>
              <button onClick={() => removeEnv(i)} className="mb-1 rounded-lg p-2.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button onClick={addEnv} variant="outline" size="sm"
          className="mt-4 border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
          <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter un point
        </Button>
      </Section>

      <Section icon={FileText} title="Documents avion (avec date de validité)">
        <EntityDocuments entityType="aircraft" entityId={id} docTypes={AIRCRAFT_DOCS} />
      </Section>
    </div>
  );
}
